import React, { useState, useContext, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Athlete } from './athlete';

import { AppContext } from '../../App';
import { dataFetcher } from '../../Utils/dataFetcher';
import { StyledSelect } from '../StyledSelect';
import { ButtonBar } from '../ButtonBar';
import TagManager from 'react-gtm-module';
import { NoData } from '../NoData';
import { filterAndSortResults } from './filter-sort-results';

import style from './results.module.scss';
import { filterAndSortApproaching } from './filter-sort-approaching';
import { CosmosText } from '../../cosmos/web-scoped/react';

let timeout;

const CheckpointResults = ({ config }) => {
  const context = React.useContext(AppContext);
  const uriConfig = context.config.uriConfig;
  const animationControls = useAnimation();

  const [listIndex, setListIndex] = useState(undefined);
  const [showAll, setShowAll] = useState(false);
  const [lastList, setLastList] = useState(undefined);
  const [dataSet, setDataSet] = useState({});
  const [option, setOption] = useState(false);
  const [currentLap, setCurrentLap] = useState(undefined);
  const [trackInfo, setTrackInfo] = useState(undefined);

  const appConfig = useContext(AppContext).config;
  const tabOpened = useContext(AppContext).inView;

  const { animation } = appConfig;

  const containerAnimation = {
    hidden: {
      opacity: 1,
      scale: 0,
      transition: {
        duration: animation.duration,
        ease: animation.ease
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: animation.duration,
        ease: animation.ease
      }
    }
  };

  useEffect(() => {
    if (timeout) {
      clearTimeout(timeout);
    }

    const setData = () => {
      config.lists.forEach(async ({ uri }, index) => {
        let curLap = currentLap;
        let lapuri;

        if (config.lists[index].laps > 1 && !currentLap) {
          for (let lap = config.lists[index].laps; lap >= 1; lap--) {
            const curList = await dataFetcher(uri.replace('{lap}', lap), uriConfig);
            if (curList && curList.results) {
              if (curList.results.length >= 20) {
                curLap = lap;
                setCurrentLap(lap);
                break;
              }
            }
          }

          if (!curLap) {
            curLap = 1;
            setCurrentLap(1);
          }
        }

        if (curLap) {
          lapuri = uri.replace('{lap}', curLap);
        }

        const data = await dataFetcher(lapuri ? lapuri : uri, uriConfig);

        if (data) {
          setDataSet((dataSet) => {
            let newDataSet = { ...dataSet };
            newDataSet[index] = { ...data };

            // check if athlete already in list
            if (data.results) {
              const checkedData = { ...data };
              checkedData.results = [];
              newDataSet[index].results.forEach((result) => {
                if (checkedData.results.length > 0) {
                  const newAthleteId = result.athlete.id + (result.lap ? result.lap : '');
                  result.athlete.id = newAthleteId;
                  const checkedAtheleteIds = [];

                  checkedData.results.forEach((checkedResult) => {
                    checkedAtheleteIds.push(checkedResult.athlete.id);
                  });
                  if (!checkedAtheleteIds.includes(newAthleteId)) {
                    checkedData.results.push(result);
                  }
                } else {
                  checkedData.results.push(result);
                }
              });
              newDataSet[index] = checkedData;
            }

            if (data && data.status && data.status === 'finished') {
              clearTimeout(timeout);
            }
            animationControls.start('visible');
            return newDataSet;
          });
        }
      });

      timeout = setTimeout(
        setData,
        config.pollRateSeconds ? config.pollRateSeconds * 1000 : 15000
      );
    };

    setData();
    animationControls.start('visible');
  }, [config, animationControls, currentLap, tabOpened, uriConfig]);

  useEffect(() => {
    if (!listIndex && dataSet[0]) {
      setLastList(dataSet[0]);
      setListIndex(0);
    }

    if (!isNaN(listIndex) && config.lists[listIndex].trackUri) {
      dataFetcher(config.lists[listIndex].trackUri, uriConfig).then((data) =>
        setTrackInfo(data.track)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSet, listIndex, uriConfig]);

  const handleChange = (data) => {
    setListIndex(data.value);
    setShowAll(false);
    setOption(data);

    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `Dropdown - ${titles[data.value].label}`,
        widgetType: `Results - ${config.trackingTitle || config.navigationTitle}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);
  };

  const handleChangeButtonBar = (data) => {
    setListIndex(data);
    setShowAll(false);
    setOption(data);

    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `Dropdown - ${titles[data].label}`,
        widgetType: `Results - ${config.trackingTitle || config.navigationTitle}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);
  };

  useEffect(() => {
    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `loaded`,
        widgetType: `Results - ${config.trackingTitle || config.navigationTitle}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkShow = () => {
    return (
      dataSet && !isNaN(listIndex) && dataSet[listIndex] && dataSet[listIndex].results
    );
  };

  const shallAnimate = (athlete) => {
    if (athlete && Boolean(config.advertising) && Boolean(lastList)) {
      const lastListAthlete = lastList.results.find(
        (knownAthlete) => knownAthlete.athlete.id === athlete.athlete.id
      );
      if (lastListAthlete && lastListAthlete.result.finishing_status === 'finished')
        return false;
      if (!lastListAthlete) return true;

      return (
        lastListAthlete.result.finishing_status !== 'finished' &&
        athlete.result.finishing_status === 'finished'
      );
    }

    return true;
  };

  const titles = config.lists.map(({ title }, index) => {
    return { label: title, value: index };
  });

  const show = config.initialNum || 20;
  const showApproach = config.approachNum || 5;

  return (
    <div className={`${style.results}`}>
      {titles.length === 1 ? (
        // if there is just one list make a title
        <h3 className="h3">{titles[0].label}</h3>
      ) : titles.length > 2 ? (
        // if there are more then two lists use StyledSelect
        <StyledSelect
          className="result-select single"
          options={titles}
          selectFirst
          onChange={handleChange}
          value={option}
          maxHeight={400}
        />
      ) : (
        // if there are just two lists use ButtonBar
        <ButtonBar
          buttons={titles.map((item, index) => {
            return {
              text: item.label,
              active: option === item.value || (index === 0 && !option),
              index: item.value
            };
          })}
          onClick={handleChangeButtonBar}
        />
      )}
      {config.lists[listIndex] && config.lists[listIndex].pagination && (
        <div className="appRow">
          <div className={`pagination`}>
            <span
              className={`${style.paginationArrowWrapper} paginationArrowWrapper`}
              onClick={() => {
                const newLapNum = currentLap >= 2 ? currentLap - 1 : 1;
                // tagLapChange(newLapNum);
                setCurrentLap(newLapNum);
              }}
              style={{
                cursor: currentLap < 1 ? 'pointer' : 'default'
              }}
            >
              {currentLap > 1 && <span className={'paginationArrow'}></span>}
            </span>

            <span className={`paginationCurrent ${style.paginationCurrent}`}>
              {trackInfo && trackInfo.checkpoints ? (
                trackInfo.checkpoints.find(({ id }) => id === currentLap).name
              ) : (
                <>LAP {currentLap}</>
              )}
            </span>
            <span
              className={`${style.paginationArrowWrapper} paginationArrowWrapper`}
              onClick={() => {
                const newLapNum =
                  currentLap <= config.lists[listIndex].laps - 1
                    ? currentLap + 1
                    : config.lists[listIndex].laps;
                // tagLapChange(newLapNum);
                setCurrentLap(newLapNum);
              }}
              style={{
                cursor: currentLap < config.lists[listIndex].laps ? 'pointer' : 'default'
              }}
            >
              {currentLap < config.lists[listIndex].laps && (
                <span className={'paginationArrow'}></span>
              )}
            </span>
          </div>
        </div>
      )}
      <div className={`appRow ${style.appRowResults}`}>
        <>
          {checkShow() && checkShow().length ? (
            <motion.table
              className={`${style.table}`}
              variants={containerAnimation}
              initial="hidden"
              animate={animationControls}
            >
              <tbody>
                {checkShow()
                  ? filterAndSortResults(dataSet[listIndex].results, config).map(
                      (athlete, index) => {
                        if (index >= show && !showAll) return undefined;
                        return (
                          <Athlete
                            start={{ order: index + 1 }}
                            {...athlete}
                            key={`${config.lists[listIndex].type}-${listIndex}-${athlete.athlete.id}`}
                            index={index}
                            animate={shallAnimate(athlete)}
                            advertising={config.advertising}
                            listType={config.lists[listIndex].type || 'result'}
                            delaySeconds={config.delayDataSeconds || 60}
                          />
                        );
                      }
                    )
                  : undefined}
              </tbody>
            </motion.table>
          ) : (
            <>
              <NoData />
            </>
          )}
        </>
      </div>
      {checkShow()
        ? filterAndSortResults(dataSet[listIndex].results, config).length > show && (
            <div className={`appRow`}>
              <button
                style={{ fontSize: "1.3rem" }}
                onClick={() => {
                  const dataLayerArgs = {
                    dataLayer: {
                      event: 'rbVideoTracking',
                      eventCategory: 'video',
                      eventAction: 'sidebar',
                      eventLabel: `${titles[listIndex].label}::Show ${
                        showAll ? 'less' : 'more'
                      }`,
                      widgetType: `Results - ${
                        config.trackingTitle || config.navigationTitle
                      }`
                    }
                  };

                  TagManager.dataLayer(dataLayerArgs);

                  setShowAll(!showAll);
                }}
              >
                {showAll ? config.showLessButton : config.showMoreButton}
              </button>
            </div>
          )
        : undefined}
      <div className={`${style.rowApproaching} appRow`}>
        <>
          {checkShow() && checkShow().length ? (
            <>
              <h4 className={`${style.headline}`}>Approaching</h4>
              <motion.table
                className={`${style.table} ${style.tableApproaching}`}
                variants={containerAnimation}
                initial="hidden"
                animate={animationControls}
              >
                <tbody>
                  {checkShow()
                    ? filterAndSortApproaching(dataSet[listIndex].results, config).map(
                        (athlete, index) => {
                          if (index >= showApproach && !showAll) return undefined;
                          return (
                            <Athlete
                              approaching
                              start={{ order: index + 1 }}
                              {...athlete}
                              key={`${config.lists[listIndex].type}-${listIndex}-${athlete.athlete.id}`}
                              index={index}
                              animate={shallAnimate(athlete)}
                              advertising={config.advertising}
                              listType={config.lists[listIndex].type || 'result'}
                              delaySeconds={config.delayDataSeconds || 60}
                            />
                          );
                        }
                      )
                    : undefined}
                </tbody>
              </motion.table>
            </>
          ) : (
            <>
              <NoData />
            </>
          )}
        </>
      </div>
      {dataSet &&
      !isNaN(listIndex) &&
      dataSet[listIndex] &&
      dataSet[listIndex].results.length &&
      (dataSet[listIndex].updated_at || dataSet[listIndex].updated) ? (
        <div className='my-8'>
          <div className="separator"></div>
          <CosmosText class="mb-2">
            last update:{' '}
            {new Date(
              dataSet[listIndex].updated || dataSet[listIndex].updated_at
            ).toLocaleTimeString()}
          </CosmosText>
        </div>
      ) : undefined}
    </div>
  );
};

export { CheckpointResults };
